/* eslint-disable complexity */
import { dFix } from '@/data/Settings';
import { formatPrice } from '@/utils/formatPrice';
import { useContext } from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import {
	OrderSummaryBreakdownDividerSx,
	OrderSummaryBreakdownFrameSx,
	OrderSummaryBreakdownTitleSx,
	OrderSummaryBreakdownTotalSx,
	defaultTypographySx,
} from '@/components/content/Cart/parts/OrderSummaryBreakdown/styles/main';
import { combineSX } from '@/utils/combineSX';
import { IsFullOfferCartAlert } from '@/components/blocks/IsFullOfferCartAlert';
import { ExtendedZoneAlert } from '@/components/blocks/ExtendedZoneAlert';
import { OrderCouponRestriction } from '@/components/blocks/OrderCouponRestriction';
import { ContentContext } from '@/data/context/content';
import { AvailableCoupon } from '@/data/Content/Cart';
import { OrderAdjustment } from '@/data/types/Order';
import { useCouponsWithoutRepurchase } from '@/data/Content/RepurchaseCouponState';
import {
	RepurchaseAppliedNotice,
	RepurchaseMiniCartCard,
} from '@/components/blocks/DpRepurchaseCoupon/parts/RepurchaseFlowCards';

interface OrderSummaryBreakdownProps {
	total: string | number;
	subtotal: string | number;
	shipping: string | number;
	discount: string | number;
	coupons: string | number;
	totalProducts?: number;
	points?: string | number;
	valeDeCaja?: string | number;
	showTitle?: boolean;
	loading?: boolean;
	confirmation?: boolean;
	isCashPayment?: boolean;
	isMiniCart?: boolean;
	noApplyFreeShippingOffer?: boolean;
	noApplyFreeShippingExtendedZone?: boolean;
	promotionCode?: {
		code?: string;
		description?: string;
		shortDescription?: string;
		longDescription?: string;
		associatedPromotion?: {
			description?: string | { value?: string; language?: string };
			promotionId?: string;
		}[];
	}[];
	availableCoupons?: AvailableCoupon[];
	withRepurchaseCartBox?: boolean;
	adjustment?: OrderAdjustment[];
}

export const OrderSummaryBreakdown: React.FC<OrderSummaryBreakdownProps> = ({
	total,
	subtotal,
	shipping,
	discount,
	coupons,
	points,
	totalProducts = 0,
	valeDeCaja,
	showTitle = true,
	loading = false,
	confirmation = false,
	isCashPayment = false,
	isMiniCart = false,
	noApplyFreeShippingOffer = false,
	noApplyFreeShippingExtendedZone = false,
	promotionCode,
	availableCoupons = [],
	withRepurchaseCartBox = false,
	adjustment,
}) => {
	const isFreeShipping = dFix(shipping || '9', 0) === 0;

	const baseTypographySx = combineSX([
		defaultTypographySx,
		isMiniCart ? { fontSize: 'var(--font-size-default)' } : {},
	]);

	const { inactivePromotionCodes } = useContext(ContentContext) as {
		inactivePromotionCodes?: string[];
	};
	const validAppliedCoupons = promotionCode ?? [];
	const activeAppliedCouponsCount = validAppliedCoupons.filter(
		({ code }) => !code || !inactivePromotionCodes?.includes(code)
	).length;
	const otherCoupons = useCouponsWithoutRepurchase(coupons, adjustment);

	return (
		<Box sx={OrderSummaryBreakdownFrameSx}>
			{isMiniCart ? null : (
				<>
					{showTitle ? (
						<Typography component={'h2'} sx={OrderSummaryBreakdownTitleSx}>
							{'RESUMEN DE TU PEDIDO'}
						</Typography>
					) : null}
					{confirmation ? null : (
						<RepurchaseAppliedNotice withCartBox={withRepurchaseCartBox} adjustment={adjustment} />
					)}
					{totalProducts > 0 ? (
						<Stack direction="row" justifyContent="space-between" sx={{ marginBottom: 2 }}>
							<Typography sx={baseTypographySx}>{`${totalProducts} producto${
								totalProducts > 1 ? 's' : ''
							}`}</Typography>
						</Stack>
					) : null}
					<Stack direction="row" justifyContent="space-between">
						<Typography sx={baseTypographySx}>{'Subtotal:'}</Typography>
						<Typography sx={baseTypographySx}>
							{loading ? '...' : formatPrice('es-MX', 'MXN', subtotal)}
						</Typography>
					</Stack>
					<Stack direction="row" justifyContent="space-between">
						<Typography sx={baseTypographySx}>{'Estás ahorrando:'}</Typography>
						<Typography sx={baseTypographySx} color={'#BF0000'}>
							{formatPrice('es-MX', 'MXN', discount)}
						</Typography>
					</Stack>
					{valeDeCaja ? (
						<Stack direction="row" justifyContent="space-between">
							<Typography sx={baseTypographySx}>{'Monedero:'}</Typography>
							<Typography sx={baseTypographySx} color={'#BF0000'}>
								{'-' + formatPrice('es-MX', 'MXN', valeDeCaja)}
							</Typography>
						</Stack>
					) : null}

					{points ? (
						<Stack direction="row" justifyContent="space-between">
							<Typography sx={combineSX([baseTypographySx, { color: '#004A9B', fontWeight: 600 }])}>
								{'Puntos canjeados:'}
							</Typography>
							<Typography sx={combineSX([baseTypographySx, { color: '#004A9B', fontWeight: 600 }])}>
								{'-' + formatPrice('es-MX', 'MXN', points ?? '0')}
							</Typography>
						</Stack>
					) : null}

					<Stack direction="row" justifyContent="space-between">
						<Typography sx={baseTypographySx}>{'Costo de envío:'}</Typography>
						<Typography sx={baseTypographySx} color={isFreeShipping ? '#0E8439' : 'inherit'}>
							{isFreeShipping ? '¡Gratis!' : formatPrice('es-MX', 'MXN', shipping)}
						</Typography>
					</Stack>
					{noApplyFreeShippingOffer ? (
						<IsFullOfferCartAlert />
					) : noApplyFreeShippingExtendedZone ? (
						<ExtendedZoneAlert />
					) : null}

					{otherCoupons > 0 || validAppliedCoupons.length ? (
						<Stack direction="column" gap="4px">
							<Stack direction="row" justifyContent="space-between">
								<Typography
									sx={combineSX([
										baseTypographySx,
										validAppliedCoupons.length ? { fontWeight: 700 } : {},
									])}
								>
									{validAppliedCoupons.length
										? `Cupones aplicados (${activeAppliedCouponsCount}):`
										: 'Cupones aplicados:'}
								</Typography>
								<Typography sx={baseTypographySx} color={'#BF0000'}>
									{formatPrice('es-MX', 'MXN', otherCoupons)}
								</Typography>
							</Stack>
							{validAppliedCoupons.map((promoItem) => {
								const couponDetail = availableCoupons.find((item) => item.code === promoItem.code);
								const code = promoItem.code;
								const shortDesc = promoItem.shortDescription || couponDetail?.shortDescription;
								return code ? (
									<OrderCouponRestriction
										key={code}
										code={code}
										promotionId={promoItem.associatedPromotion?.[0]?.promotionId}
										shortDescription={shortDesc}
										minimumPurchase={couponDetail?.minimumPurchase}
										currentSubtotal={Number(subtotal)}
									/>
								) : null;
							})}
						</Stack>
					) : null}

					<Divider sx={OrderSummaryBreakdownDividerSx} />
				</>
			)}

			{isMiniCart && !confirmation ? <RepurchaseMiniCartCard adjustment={adjustment} /> : null}
			<Stack direction="row" justifyContent="space-between" alignItems={'center'}>
				<Typography
					sx={combineSX([
						{ fontSize: '15px', fontWeight: 800, textTransform: 'uppercase' },
						isMiniCart
							? {
									fontSize: 'var(--font-size-default)',
									fontWeight: 800,
									textTransform: 'capitalize',
							  }
							: {},
					])}
				>
					{confirmation && isCashPayment ? 'Total a pagar:' : 'TOTAL:'}
				</Typography>
				<Typography
					sx={combineSX([
						OrderSummaryBreakdownTotalSx,
						confirmation && isCashPayment ? { color: '#399300', fontSize: '18px' } : {},
						isMiniCart ? { fontSize: 'var(--font-size-default)' } : {},
					])}
				>
					{formatPrice('es-MX', 'MXN', total)}
				</Typography>
			</Stack>
		</Box>
	);
};
